/**
 * Smart ink smoothing (SRS C5).
 *
 * A mouse, a trackpad and an unsteady hand all leave the same mark: a line that
 * should be smooth arrives as a row of tiny zigzags. This takes those out.
 *
 * The hard part is what it must NOT do. Handwriting is made of small deliberate
 * movements that look exactly like jitter to a smoother, so every sample here is
 * allowed to move only a fraction of the distance to the next one. Jitter, which is
 * smaller than that, disappears; a letter, which is not, survives — and the two ends
 * of the stroke never move at all, because that is where the hand actually pressed
 * and lifted.
 *
 * It runs when the hand lifts, never while it is down: the pen path is sacred
 * (docs/07-DESIGN-SYSTEM.md §7.3), and ink that shifted under a moving nib would be
 * worse than any jitter.
 */

import type { InkPoint } from './ink.js';

/** How many times the averaging runs. Two is enough to kill sampling zigzag. */
export const SMOOTHING_PASSES = 2;

/**
 * How far one pass may move a sample, as a share of the gap between samples.
 *
 * This is the whole guard on letterforms. Below half a sample gap, a movement is
 * smaller than the hand could have meant.
 */
export const MAX_SHIFT_RATIO = 0.45;

/** The average distance between one sample and the next. */
function meanGap(points: readonly InkPoint[]): number {
  let total = 0;
  let steps = 0;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (!previous || !current) continue;
    total += Math.hypot(current.x - previous.x, current.y - previous.y);
    steps += 1;
  }
  return steps === 0 ? 0 : total / steps;
}

/** `towards`, but never further than `limit` from where it started. */
function capped(from: InkPoint, x: number, y: number, limit: number): InkPoint {
  const dx = x - from.x;
  const dy = y - from.y;
  const moved = Math.hypot(dx, dy);
  if (moved <= limit || moved === 0) return { x, y, pressure: from.pressure };
  const share = limit / moved;
  return { x: from.x + dx * share, y: from.y + dy * share, pressure: from.pressure };
}

/**
 * The same stroke with its jitter taken out and its shape left alone.
 *
 * Pressure is carried through untouched: how hard the nib was pressed at a point is
 * a reading from the tablet, not something to average away.
 */
export function smoothInk(points: readonly InkPoint[]): InkPoint[] {
  const copied = points.map((point) => ({ x: point.x, y: point.y, pressure: point.pressure }));
  if (copied.length < 3) return copied;

  const limit = meanGap(copied) * MAX_SHIFT_RATIO;
  if (limit <= 0) return copied;

  let current = copied;
  for (let pass = 0; pass < SMOOTHING_PASSES; pass += 1) {
    const source = current;
    current = source.map((point, index) => {
      const before = source[index - 1];
      const after = source[index + 1];
      // The ends are where the hand pressed and where it lifted. They stay put.
      if (!before || !after) return point;
      return capped(point, (before.x + point.x * 2 + after.x) / 4, (before.y + point.y * 2 + after.y) / 4, limit);
    });
  }
  return current;
}
